import { create } from "zustand";
import { persist } from "zustand/middleware";
import indexedDBStorage from "./indexedDBStorage";

const useUserStore = create(
  persist(
    (set, get) => ({
      // User State (Persisted)
      userId: null,
      firstName: null,
      lastName: null,
      userName: null,
      userRole: null,
      isAuthenticated: false,
      primaryProfileId: null,
      primaryProfileName: null,
      profileList: [],
      currentProfile: null,
      profileRole: null,
      communityId: null,
      groupId: null,

      hasHydrated: false,
      setHasHydrated: () => set({ hasHydrated: true }),

      // Functions (NOT Persisted)
      login: ({
        userId,
        firstName,
        lastName,
        userRole,
        primaryProfileId,
        primaryProfileName,
        isAuthenticated,
        profileRole,
        communityId,
        groupId,
        currentProfile,
      }) =>
        set((state) => {
          const newState = {
            ...state,
            userId: userId ?? state.userId,
            firstName: firstName ?? state.firstName,
            lastName: lastName ?? state.lastName,
            userName: [firstName, lastName].filter(Boolean).join(" ") || null,
            userRole: userRole ?? state.userRole,
            isAuthenticated: true,
            primaryProfileId: primaryProfileId ?? state.primaryProfileId,
            primaryProfileName: primaryProfileName ?? state.primaryProfileName,
            profileRole: profileRole ?? state.profileRole,
            communityId: communityId ?? state.communityId,
            groupId: groupId ?? state.groupId,
            currentProfile: {
              profileId: primaryProfileId,
              name: primaryProfileName,
            },
          };
          // console.log("Updated User Store:", newState);
          return newState;
        }),

      updateUserStore: (newData) =>
        set((state) => {
          const newState = {
            ...state,
            ...newData,
            userName:
              [
                newData.firstName ?? state.firstName,
                newData.lastName ?? state.lastName,
              ]
                .filter(Boolean)
                .join(" ") || null,
          };
          // console.log("Updated User Store:", newState);
          return newState;
        }),

      updateProfiles: (profiles) =>
        set((state) => {
          const newState = { ...state, profileList: profiles };
          // console.log("Updated User Store:", newState);
          return newState;
        }),

      setCurrentProfile: (profile) =>
        set((state) => ({
          ...state,
          currentProfile: profile
            ? { profileId: profile.id, name: profile.name }
            : null, // Ensure profileId is stored
        })),

      setProfileRole: (role) =>
        set((state) => {
          const newState = { ...state, profileRole: role };
          // console.log("Updated User Store:", newState);
          return newState;
        }),

      setCommunityAndGroup: (communityId, groupId) =>
        set((state) => {
          const newState = {
            ...state,
            communityId: communityId ?? null,
            groupId: groupId ?? null,
          };
          // console.log("Updated User Store:", newState);
          return newState;
        }),

      logout: () =>
        set(() => {
          const newState = {
            userId: null,
            firstName: null,
            lastName: null,
            userName: null,
            userRole: null,
            isAuthenticated: false,
            primaryProfileId: null,
            primaryProfileName: null,
            profileList: [],
            currentProfile: null,
            profileRole: null,
            communityId: null,
            groupId: null,
          };
          // console.log("User Logged Out:", newState);
          return newState;
        }, true),
    }),
    {
      name: "user-storage", // Key for IndexedDB storage
      storage: indexedDBStorage, // Use IndexedDB
      partialize: (state) => ({
        // Only persist serializable fields
        userId: state.userId,
        firstName: state.firstName,
        lastName: state.lastName,
        userName: state.userName,
        userRole: state.userRole,
        isAuthenticated: state.isAuthenticated,
        primaryProfileId: state.primaryProfileId,
        primaryProfileName: state.primaryProfileName,
        profileList: state.profileList,
        currentProfile: state.currentProfile,
        profileRole: state.profileRole,
        communityId: state.communityId,
        groupId: state.groupId,
      }),
      onRehydrateStorage: () => (state) => {
        state.setHasHydrated();
      },
    }
  )
);

// Subscribe to log state on every update (logs changes even if done outside of functions)
useUserStore.subscribe((state) => {
  // console.log("User Store Updated:", state);
});

export default useUserStore;
